import { useState } from 'react';
import { X, Smartphone, Mail, Shield } from 'lucide-react';

export type MFAMethod = 'phone' | 'email' | 'authenticator';

interface MFAMethodSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onMethodSelect: (method: MFAMethod) => void;
  title?: string;
  subtitle?: string;
}

export default function MFAMethodSelector({
  isOpen,
  onClose,
  onMethodSelect,
  title = "Choose Verification Method",
  subtitle = "Select how you'd like to receive your verification code"
}: MFAMethodSelectorProps) {
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod | null>(null);

  if (!isOpen) return null;

  const methods = [
    {
      id: 'phone' as MFAMethod,
      name: 'Phone Number',
      description: 'Receive SMS codes on your phone',
      icon: Smartphone,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800'
    },
    {
      id: 'email' as MFAMethod,
      name: 'Email',
      description: 'Receive codes via email',
      icon: Mail,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800'
    },
    {
      id: 'authenticator' as MFAMethod,
      name: 'Authenticator App',
      description: 'Use Google Authenticator, Authy, or similar',
      icon: Shield,
      iconColor: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800'
    }
  ];

  const handleMethodClick = (method: MFAMethod) => {
    setSelectedMethod(method);
  };

  const handleContinue = () => {
    if (selectedMethod) {
      onMethodSelect(selectedMethod);
      setSelectedMethod(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Method Options */}
        <div className="p-6 space-y-3">
          {methods.map((method) => {
            const Icon = method.icon;
            const isSelected = selectedMethod === method.id;
            
            return (
              <button
                key={method.id}
                onClick={() => handleMethodClick(method.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? `${method.borderColor} ${method.bgColor} ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-current`
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${isSelected ? method.bgColor : 'bg-gray-100 dark:bg-gray-600'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? method.iconColor : 'text-gray-600 dark:text-gray-300'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                      {method.name}
                    </h3>
                    <p className={`text-sm mt-1 ${isSelected ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {method.description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="flex-shrink-0">
                      <div className={`w-5 h-5 rounded-full ${method.iconColor.replace('text-', 'bg-')} flex items-center justify-center`}>
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedMethod}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              selectedMethod
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
